import { stagger } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { Card } from '@/components/ui/card';
import type { ReadinessItem } from './steps';

/** "Readiness" with how many checks pass. */
function ReadinessHeader({ subject, passed, total }: { subject: string; passed: number; total: number }) {
  return (
    <div className="flex items-start justify-between gap-3 p-4">
      <div className="min-w-0">
        <Kicker className="text-[11px]">Readiness</Kicker>
        <p className="mt-0.5 break-words text-xs text-subtle">{subject} setup checklist</p>
      </div>
      <span className="tabular shrink-0 rounded-md border px-2 py-1 font-mono text-xs text-foreground">
        {passed} / {total} OK
      </span>
    </div>
  );
}

function PassedList({ passed }: { passed: ReadinessItem[] }) {
  if (passed.length === 0) return <p className="mt-2 text-[13px] text-muted-foreground">Nothing yet: start with step 1.</p>;
  return (
    <ul className="mt-2.5 flex flex-col gap-2">
      {passed.map((i) => (
        <li key={i.key} data-testid={`gs-ready-${i.key}`} className="flex items-start gap-2 text-[13px] text-foreground">
          <Icon name="check_circle" size={16} className="mt-0.5 shrink-0 text-succeeded" />
          <span className="min-w-0 break-words">{i.label}</span>
        </li>
      ))}
    </ul>
  );
}

/** A missing check: jumps to its step. */
function MissingItem({ item }: { item: ReadinessItem }) {
  return (
    <li>
      <a href={`#gs-step-${item.step}`} data-testid={`gs-missing-${item.key}`} className="lift flex items-start gap-2 rounded-lg border border-awaiting/25 bg-awaiting/5 px-3 py-2 hover:bg-awaiting/10">
        <Icon name={item.key.startsWith('missing-') ? 'warning' : 'radio_button_unchecked'} size={16} className="mt-0.5 shrink-0 text-awaiting" />
        <span className="min-w-0">
          <span className="block break-words text-[13px] font-medium text-foreground">{item.label}</span>
          {item.detail ? <span className="block text-xs text-muted-foreground">{item.detail}</span> : null}
        </span>
      </a>
    </li>
  );
}

function MissingList({ missing }: { missing: ReadinessItem[] }) {
  if (missing.length === 0) {
    return (
      <p className="mt-2 flex items-center gap-1.5 text-[13px] text-muted-foreground">
        <Icon name="verified" size={16} className="text-succeeded" />
        Nothing missing: ready to build.
      </p>
    );
  }
  return (
    <ul className="mt-2.5 flex flex-col gap-2">
      {missing.map((i) => (
        <MissingItem key={i.key} item={i} />
      ))}
    </ul>
  );
}

/**
 * "Readiness": the checks behind the steps (steps.ts `readinessOf`, the same facts), passed first, then what is still
 * missing; each missing item jumps to its step. Right rail from 1280 px, under the cards below.
 */
export function ReadinessRail({ items, subject, className }: { items: ReadinessItem[]; subject: string; className?: string }) {
  const passed = items.filter((i) => i.ok);
  const missing = items.filter((i) => !i.ok);
  return (
    <Card data-testid="gs-readiness" data-ok={passed.length} data-total={items.length} className={cn('enter gap-0 p-0', className)} style={stagger(3)}>
      <ReadinessHeader subject={subject} passed={passed.length} total={items.length} />
      <section className="border-t p-4" aria-label="Checks passed">
        <Kicker className="text-[11px] text-succeeded">
          <span aria-hidden className="size-1.5 rounded-full bg-succeeded" />
          Checks passed
        </Kicker>
        <PassedList passed={passed} />
      </section>
      <section className="border-t p-4" aria-label="Still missing">
        <Kicker className="text-[11px] text-awaiting">
          <span aria-hidden className="size-1.5 rounded-full bg-awaiting" />
          Still missing
        </Kicker>
        <MissingList missing={missing} />
      </section>
    </Card>
  );
}
